import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import axios from "axios"
import AppLayout from "@/components/app/AppLayout"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { API_BASE } from "@/config/api"
import { HrSubnav } from "./HrSubnav"
import { toast } from "sonner"

const API = API_BASE

export default function HrEmployeeDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [contractForm, setContractForm] = useState({
    template_id: "cim_part_time_generala",
    contract_type: "indeterminata",
    work_fraction: 0.5,
    gross_salary: 4050,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
  })
  const [uploadKey, setUploadKey] = useState("ci")
  const [regesToken, setRegesToken] = useState("")

  const load = () => {
    axios.get(`${API}/hr/employees/${id}`).then((r) => setData(r.data)).catch(() => {})
  }

  useEffect(() => { load() }, [id])

  const createContract = async () => {
    try {
      const { data: c } = await axios.post(`${API}/hr/contracts`, { ...contractForm, employee_id: id })
      const { data: pdf } = await axios.post(`${API}/hr/contracts/${c.id}/generate`)
      if (pdf.pdf_base64) {
        const a = document.createElement("a")
        a.href = `data:application/pdf;base64,${pdf.pdf_base64}`
        a.download = `CIM-${c.id.slice(0, 8)}.pdf`
        a.click()
      }
      toast.success("Contract generat")
      load()
    } catch {
      toast.error("Eroare contract")
    }
  }

  const submitReges = async () => {
    try {
      const contractId = data?.contracts?.[0]?.id
      const { data: res } = await axios.post(`${API}/hr/reges/submit`, {
        employee_id: id,
        action_type: "hire",
        contract_id: contractId,
      })
      if (res.demo || !res.ok) {
        toast.error(res.detail || "REGES blocat — configurează tokenul (REGES_DEMO_MODE trebuie false)")
      } else {
        toast.success("Trimis în REGES")
      }
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare REGES — token lipsă sau mod demo")
    }
  }

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append("file", file)
    try {
      await axios.post(`${API}/hr/employees/${id}/documents/upload?doc_key=${uploadKey}`, fd)
      toast.success("Document încărcat")
      load()
    } catch {
      toast.error("Upload eșuat")
    }
  }

  const connectReges = async () => {
    try {
      await axios.post(`${API}/hr/reges/connect`, { token: regesToken })
      toast.success("REGES conectat")
    } catch {
      toast.error("Conectare eșuată")
    }
  }

  if (!data) return <AppLayout title={t("app.hr.employee.title")}><HrSubnav /><p className="text-muted-foreground">{t("app.hr.employee.loading")}</p></AppLayout>

  const emp = data.employee

  return (
    <AppLayout title={`${emp.first_name} ${emp.last_name}`}>
      <HrSubnav />
      <Button variant="ghost" size="sm" className="mb-4" asChild><Link to="/app/hr/employees">← Înapoi</Link></Button>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-border p-5 space-y-3">
          <h2 className="font-medium">Date angajat</h2>
          <p className="text-sm"><span className="text-muted-foreground">CNP:</span> {emp.cnp_masked}</p>
          <p className="text-sm"><span className="text-muted-foreground">Funcție:</span> {emp.role_code}</p>
          <p className="text-sm"><span className="text-muted-foreground">Email:</span> {emp.email || "—"}</p>
        </section>

        <section className="rounded-xl border border-border p-5">
          <h2 className="font-medium mb-3">Dosar — checklist</h2>
          <ul className="space-y-2 text-sm">
            {(data.checklist || []).map((item) => (
              <li key={item.doc_key} className="flex items-center gap-2">
                <span className={item.uploaded ? "text-green-600" : "text-amber-600"}>{item.uploaded ? "✓" : "○"}</span>
                {item.label}
                {item.expires_at && <span className="text-xs text-muted-foreground">exp. {item.expires_at.slice(0, 10)}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Tip act</Label>
              <select className="h-9 rounded-md border px-2 text-sm" value={uploadKey} onChange={(e) => setUploadKey(e.target.value)}>
                {(data.checklist || []).map((c) => <option key={c.doc_key} value={c.doc_key}>{c.label}</option>)}
              </select>
            </div>
            <Input type="file" onChange={uploadDoc} className="max-w-xs" />
          </div>
        </section>

        <section className="rounded-xl border border-border p-5 space-y-3 lg:col-span-2">
          <h2 className="font-medium">Contract + REGES</h2>
          <PilotBanner>
            REGES necesită token real și REGES_DEMO_MODE=false. Fără acestea, trimiterea este blocată (nu se simulează acceptare).
          </PilotBanner>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Șablon</Label>
              <select className="w-full h-10 border rounded-md px-2 text-sm" value={contractForm.template_id} onChange={(e) => setContractForm({ ...contractForm, template_id: e.target.value })}>
                <option value="cim_part_time_generala">CIM part-time</option>
                <option value="cim_indeterminata_receptioner">CIM recepționer</option>
                <option value="cim_determinata_asistent_medical">CIM determinat asistent</option>
                <option value="colaborare_pfa">Colaborare PFA</option>
              </select>
            </div>
            <div><Label>Salariu brut</Label><Input type="number" value={contractForm.gross_salary} onChange={(e) => setContractForm({ ...contractForm, gross_salary: +e.target.value })} /></div>
            <div><Label>Normă (0-1)</Label><Input type="number" step="0.1" max="1" value={contractForm.work_fraction} onChange={(e) => setContractForm({ ...contractForm, work_fraction: +e.target.value })} /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={createContract}>Generează CIM (PDF)</Button>
            <Button size="sm" variant="secondary" onClick={submitReges}>Trimite în REGES</Button>
          </div>
          <div className="flex gap-2 items-end pt-2 border-t border-border">
            <div className="flex-1"><Label className="text-xs">Token REGES (din cont angajator)</Label><Input value={regesToken} onChange={(e) => setRegesToken(e.target.value)} placeholder="OAuth token" /></div>
            <Button size="sm" variant="outline" onClick={connectReges}>Conectează REGES</Button>
          </div>
          {(data.reges_submissions || []).slice(0, 5).map((s) => (
            <p key={s.id} className="text-xs text-muted-foreground">{s.action_type} — {s.status} {s.message_id && `(${s.message_id})`}</p>
          ))}
        </section>

        <section className="rounded-xl border border-border p-5 lg:col-span-2">
          <h2 className="font-medium mb-2">Onboarding</h2>
          <ul className="text-sm space-y-2">
            {(data.onboarding || []).map((t) => (
              <li key={t.id} className="flex justify-between items-center border-b border-border/50 py-2">
                <span>{t.title} {t.completed_at && <span className="text-green-600 text-xs">— finalizat</span>}</span>
                {!t.completed_at && (
                  <Button size="sm" variant="outline" onClick={async () => {
                    await axios.post(`${API}/hr/onboarding/${t.id}/complete`, {
                      signature_data: JSON.stringify({
                        signed_in_app: true,
                        signed_at: new Date().toISOString(),
                        task_id: t.id,
                      }),
                    })
                    toast.success("Task finalizat")
                    load()
                  }}>Marchează finalizat</Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppLayout>
  )
}
